// Walk-in timing for lab drop points in Create Order. A drop point's opening hours and walk-in
// window are the lab's local wall-clock times, so "now" is read in the lab's timezone — not the
// device's. lab-cloud already treats the platform as Copenhagen-only (shared/sameDayDeadline.ts's
// LAB_TIME_ZONE); this mirrors it, and mirrors lab-customer's labDropPointAvailability.ts, which
// applies the same rule to the customer app (MIL-479). When labs outside Copenhagen are
// onboarded the zone becomes a LabAccount field and all three read it from there.
import type { DropPoint } from '@team-milva/lab-models/resources'

type TWeekdayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday'

const LAB_TIME_ZONE = 'Europe/Copenhagen'

const labClockFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: LAB_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

// IDropPointOpeningHours only covers Monday–Friday, so a weekend has no key.
const WEEKDAY_KEY_BY_SHORT_NAME: Record<string, TWeekdayKey> = {
  Mon: 'monday',
  Tue: 'tuesday',
  Wed: 'wednesday',
  Thu: 'thursday',
  Fri: 'friday',
}

export interface ILabLocalClock {
  // "YYYY-MM-DD" — the format ClosedDay.date uses.
  isoDate: string
  // null on a weekend.
  weekdayKey: TWeekdayKey | null
  minutesSinceMidnight: number
}

export function getLabLocalClock(now: Date): ILabLocalClock {
  const parts = labClockFormatter.formatToParts(now)
  const get = (type: string): string => parts.find((x) => x.type === type)?.value ?? ''
  return {
    isoDate: `${get('year')}-${get('month')}-${get('day')}`,
    weekdayKey: WEEKDAY_KEY_BY_SHORT_NAME[get('weekday')] ?? null,
    minutesSinceMidnight: Number(get('hour')) * 60 + Number(get('minute')),
  }
}

// Open on the lab's today — a weekday the drop point is open and that is not one of the lab's
// closed dates — and not yet past its closing time.
export function isDropPointOpenToday(dp: DropPoint, closedDates: string[], now: Date = new Date()): boolean {
  const { weekdayKey, isoDate, minutesSinceMidnight } = getLabLocalClock(now)
  if (!weekdayKey) return false
  if (closedDates.includes(isoDate)) return false
  const todayHours = dp.openingHours[weekdayKey]
  if (!todayHours.isOpen) return false
  const closeMinutes = todayHours.close.hours * 60 + todayHours.close.minutes
  return minutesSinceMidnight < closeMinutes
}

export function isWithinDropPointWalkInWindow(dp: DropPoint, now: Date = new Date()): boolean {
  if (!dp.walkInWindow) return false
  const { minutesSinceMidnight } = getLabLocalClock(now)
  const startMinutes = dp.walkInWindow.start.hours * 60 + dp.walkInWindow.start.minutes
  const endMinutes = dp.walkInWindow.end.hours * 60 + dp.walkInWindow.end.minutes
  return minutesSinceMidnight >= startMinutes && minutesSinceMidnight < endMinutes
}
